import type { ApiError, CreateRoomRequest, CreateRoomResponse } from '../../shared/protocol.ts';

export type ApiFailure = ApiError['error'] | 'network';

export class ApiRequestError extends Error {
  readonly reason: ApiFailure;
  constructor(reason: ApiFailure) {
    super(reason);
    this.reason = reason;
  }
}

export async function createRoom(request: CreateRoomRequest): Promise<CreateRoomResponse> {
  let res: Response;
  try {
    res = await fetch('/api/rooms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  } catch {
    throw new ApiRequestError('network');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new ApiRequestError(body?.error ?? 'unavailable');
  }
  const created = (await res.json()) as CreateRoomResponse;
  saveSeatToken(created.code, created.token);
  return created;
}

export async function roomExists(code: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/rooms/${encodeURIComponent(code)}`);
    return res.ok;
  } catch {
    throw new ApiRequestError('network');
  }
}

// Seat tokens let a player reclaim their seat after a refresh, a dropped
// connection or closing the tab and opening the link again.
//
// - sessionStorage: the token this tab plays with (survives a refresh).
// - localStorage: every seat this browser holds per room, so a new tab can take
//   the seat back once the old tab is gone.
// - Web Locks: the tab playing a seat holds a lock on it. A second tab that finds
//   the lock taken joins as a new player instead, so one browser can still play
//   both sides of a friend game in two tabs.
const seatKey = (code: string) => `outchess:seat:${code}`;
const seatsKey = (code: string) => `outchess:seats:${code}`;
const SEATS_PREFIX = 'outchess:seats:';
/** Rooms are deleted after 30 idle minutes; a day is plenty to come back. */
const SEAT_MEMORY_MS = 24 * 60 * 60_000;

interface StoredSeats {
  tokens: string[];
  at: number;
}

function readSeats(code: string): string[] {
  try {
    const raw = window.localStorage.getItem(seatsKey(code));
    if (!raw) return [];
    const stored = JSON.parse(raw) as StoredSeats;
    return Array.isArray(stored.tokens) ? stored.tokens.filter((t) => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

function rememberSeat(code: string, token: string) {
  try {
    const now = Date.now();
    // Forget seats of old rooms.
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(SEATS_PREFIX)) continue;
      try {
        const stored = JSON.parse(window.localStorage.getItem(key) ?? '') as StoredSeats;
        if (!(now - stored.at < SEAT_MEMORY_MS)) window.localStorage.removeItem(key);
      } catch {
        window.localStorage.removeItem(key);
      }
    }
    const tokens = [token, ...readSeats(code).filter((t) => t !== token)].slice(0, 2);
    window.localStorage.setItem(seatsKey(code), JSON.stringify({ tokens, at: now } satisfies StoredSeats));
  } catch {
    // storage blocked: the seat can only be reclaimed from this tab
  }
}

const heldSeats = new Set<string>();

/** Hold a lock on `token` for as long as this tab lives (released automatically when it closes). */
function holdSeat(token: string): Promise<boolean> {
  if (heldSeats.has(token)) return Promise.resolve(true);
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    locks
      .request(`outchess-seat:${token}`, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        heldSeats.add(token);
        resolve(true);
        return new Promise<void>(() => {}); // keep it until the tab goes away
      })
      .catch(() => resolve(true));
  });
}

/** The token this tab already plays with in `code`, if any. */
export function loadSeatToken(code: string): string | null {
  try {
    return window.sessionStorage.getItem(seatKey(code));
  } catch {
    return null;
  }
}

/**
 * The seat to claim when opening a room: this tab's own token, otherwise one this
 * browser held earlier and no other open tab is using right now.
 */
export async function resolveSeatToken(code: string): Promise<string | null> {
  const own = loadSeatToken(code);
  if (own) {
    void holdSeat(own);
    return own;
  }
  for (const token of readSeats(code)) {
    if (await holdSeat(token)) {
      try {
        window.sessionStorage.setItem(seatKey(code), token);
      } catch {
        // fine: the token is still used for this connection
      }
      return token;
    }
  }
  return null;
}

export function saveSeatToken(code: string, token: string) {
  try {
    window.sessionStorage.setItem(seatKey(code), token);
  } catch {
    // ignore: the seat simply cannot be reclaimed after a refresh
  }
  rememberSeat(code, token);
  void holdSeat(token);
}
